// The page-wide Filter button (docs/app.md §5) — lives in the header among the
// queue tools. The button shows how many filter groups are active (and goes
// brand-colored); it opens the centered Filters dialog (D-344 — it was a
// popover in the header's corner until then): Companies and Vendors, each a
// multi-select (owner, 2026-10-14), Invoice Date
// From and To, Total From and To, then Reset and Apply. Queue screens filter their loaded rows in
// memory (lib/filters.ts); Archives sends the same filter to the API as named
// parameters. The company and vendor option lists are derived by the parent from
// the rows behind the timeline badges, so they are naturally limited to what the
// user may see and never the whole vendor master. Each option carries the id as
// well as the name, because the API filters on the id.
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Field,
  Input,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { FilterRegular } from '@fluentui/react-icons'
import { type FilterPick, type InvoiceFilter, emptyFilter, filterPickKey as keyOf } from '../lib/filters'
import { useMoneyInputStyles } from '../lib/theme'
import { FilterDialog } from './FilterDialog'
import { useDialogFormStyles } from './dialogForm'
import { MultiSelectField, type MultiSelectOption } from './MultiSelectField'

const useStyles = makeStyles({
  // Clear the inputs' own minimum width so each fills its grid cell.
  control: { width: '100%', minWidth: 0 },
  // Lighten the native date input's mm/dd/yyyy text to match the other placeholders.
  // The format text is drawn by the webkit datetime-edit pseudo-element, which
  // doesn't pick up the input's `color`, so target it directly.
  //
  // `Foreground3`, not `4`, since 2026-09-11 (owner): this is help text, and
  // help text is `colorNeutralForeground3` everywhere else in the app. It was
  // the odd one out here and on Analytics → Reporting, whose own date fields
  // moved in the same change — the owner's ask was that the greys "should match
  // all other help text on-screen", and two screens drawing the same
  // `mm/dd/yyyy` in two greys is the same complaint one level up.
  dateInput: {
    '& input::-webkit-datetime-edit': { color: tokens.colorNeutralForeground3 },
  },
  anyHint: { color: tokens.colorNeutralForeground3 },
})

/** One choice in the company or vendor list: what the user reads, and the
    id the API filters on. */
type FilterOption = FilterPick

/** The choices, keyed by `filterPickKey`. Two vendors that share a name are told
    apart by the id beside it (code review D-03, 2026-10-17). */
function toChoices(options: readonly FilterOption[]): MultiSelectOption[] {
  const seen = new Map<string, number>()
  for (const o of options) seen.set(o.name, (seen.get(o.name) ?? 0) + 1)
  return options.map((o) => ({
    value: keyOf(o),
    label: (seen.get(o.name) ?? 0) > 1 && o.id !== '' ? `${o.name} (${o.id})` : o.name,
  }))
}

/** The picks for a list's selected keys. A pick no longer offered (its rows have
    left the queues) is kept as it was, so it still filters and can be cleared. */
function picksFor(keys: readonly string[], options: readonly FilterOption[], current: readonly FilterPick[]): FilterPick[] {
  return keys.flatMap((k) => {
    const found = options.find((o) => keyOf(o) === k) ?? current.find((p) => keyOf(p) === k)
    return found === undefined ? [] : [{ id: found.id, name: found.name }]
  })
}

/**
 * A-Z, case-insensitively, however the caller handed them over (owner,
 * 2026-09-03 — the Company dropdown is to be alphabetical).
 *
 * `vendorFilterOptions` and `companyFilterOptions` already sort, so today this changes nothing. It is
 * here anyway because "alphabetical" is a property of THIS dropdown, and
 * leaving it as a promise every caller has to remember is how a second call
 * site ships an unsorted list without failing anything. `localeCompare` rather
 * than `<`, so "SeniorLIFE Altoona" and "Seniors at Home" order the way a
 * reader expects instead of by code point.
 */
function alphabetical(options: readonly FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

interface Props {
  companies: FilterOption[]
  vendors: FilterOption[]
  /** Render the trigger as a compact icon button (header tool group) instead of
      a labeled button. The active-group count moves into the tooltip/label and
      the button goes brand-colored when any filter is set. */
  iconOnly?: boolean
  value: InvoiceFilter
  onChange: (f: InvoiceFilter) => void
}

/**
 * What an EMPTY date field shows before the browser's own `mm/dd/yyyy`: "Any",
 * in the help-text grey — the Dashboard's filter does the same (owner,
 * 2026-10-09: every Filter modal shares one structure). Only while empty.
 */
function anyPrefix(value: string, className: string) {
  return value === '' ? <span className={className}>Any</span> : null
}

export function FilterButton({ companies, vendors, iconOnly = false, value, onChange }: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const ms = useMoneyInputStyles()
  const [open, setOpen] = useState(false)
  // THE DRAFT (owner, 2026-10-09: "Setting should not be applied unless the
  // Apply button is pressed"): re-seeded from the applied filter each time the
  // dialog opens, so closing it any other way discards the edits.
  const [draft, setDraft] = useState<InvoiceFilter>(value)
  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  // Both lists read A-Z. Memoized on the props rather than sorted inline in
  // the JSX, so a re-render from typing in the amount fields does not re-sort
  // thirty-eight companies on every keystroke.
  const companyOptions = useMemo(() => alphabetical(companies), [companies])
  const vendorOptions = useMemo(() => alphabetical(vendors), [vendors])
  // A pick whose rows have left the queues is no longer in the options; it is
  // listed anyway so the user can see it and clear it.
  const companyChoices = useMemo(
    () => toChoices(alphabetical([...companyOptions, ...draft.companies.filter((p) => !companyOptions.some((o) => keyOf(o) === keyOf(p)))])),
    [companyOptions, draft.companies],
  )
  const vendorChoices = useMemo(
    () => toChoices(alphabetical([...vendorOptions, ...draft.vendors.filter((p) => !vendorOptions.some((o) => keyOf(o) === keyOf(p)))])),
    [vendorOptions, draft.vendors],
  )

  const activeGroups = [
    value.companies.length > 0,
    value.vendors.length > 0,
    value.fromDate || value.toDate,
    value.minTotal || value.maxTotal,
  ].filter(Boolean).length
  const filterLabel = activeGroups > 0 ? `Filter (${activeGroups})` : 'Filter'

  function set<K extends keyof InvoiceFilter>(key: K, v: string) {
    setDraft((prev) => ({ ...prev, [key]: v }))
  }

  // A company or vendor choice keeps both halves of each pick: the name the
  // dialog shows and the id the query uses.
  function pick(group: 'companies' | 'vendors', keys: string[]) {
    const options = group === 'companies' ? companyOptions : vendorOptions
    setDraft((prev) => ({ ...prev, [group]: picksFor(keys, options, prev[group]) }))
  }

  return (
    <>
      {iconOnly ? (
        // A Fluent <Tooltip>, not the native `title` attribute this used to
        // carry (owner item 1, 2026-08-31). Every sibling in the header
        // cluster — Upload, User Guide, Custom Notifications, Refresh, Pipeline —
        // is `<Tooltip content=… relationship="label">`, so a `title` here
        // rendered the browser's own tooltip instead: different delay,
        // different position, different chrome. `relationship="label"` gives
        // the button its accessible name, exactly as it does for the others.
        <Tooltip content={filterLabel} relationship="label">
          <Button
            appearance={activeGroups > 0 ? 'primary' : 'secondary'}
            icon={<FilterRegular />}
            onClick={() => setOpen(true)}
          />
        </Tooltip>
      ) : (
        <Button appearance={activeGroups > 0 ? 'primary' : 'secondary'} icon={<FilterRegular />} onClick={() => setOpen(true)}>
          Filter{activeGroups > 0 ? ` (${activeGroups})` : ''}
        </Button>
      )}
      <FilterDialog
        open={open}
        onClose={() => setOpen(false)}
        onReset={() => setDraft(emptyFilter)}
        onApply={() => onChange(draft)}
      >
        {(host) => (
          <div className={f.grid2}>
            {/* MULTI-SELECT (owner, 2026-10-14): a row passes when it
                matches any pick. Empty reads "Any", in the help-text grey
                (owner, 2026-10-09). */}
            <MultiSelectField
              popupHost={host}
              label="Companies"
              options={companyChoices}
              selected={draft.companies.map(keyOf)}
              onChange={(keys) => pick('companies', keys)}
            />
            <MultiSelectField
              popupHost={host}
              label="Vendors"
              options={vendorChoices}
              selected={draft.vendors.map(keyOf)}
              onChange={(keys) => pick('vendors', keys)}
            />
            {/* A RANGE IS TWO NAMED FIELDS, From and To (owner, 2026-10-09:
                "Use language like 'Invoice Date From' and 'Invoice Date To'
                when ranges are being used"), with no Min or Max inside. */}
            <Field label="Invoice Date From">
              <Input
                className={mergeClasses(s.control, draft.fromDate === '' && s.dateInput)}
                type="date"
                contentBefore={anyPrefix(draft.fromDate, s.anyHint)}
                value={draft.fromDate}
                onChange={(_, d) => set('fromDate', d.value)}
              />
            </Field>
            <Field label="Invoice Date To">
              <Input
                className={mergeClasses(s.control, draft.toDate === '' && s.dateInput)}
                type="date"
                contentBefore={anyPrefix(draft.toDate, s.anyHint)}
                value={draft.toDate}
                onChange={(_, d) => set('toDate', d.value)}
              />
            </Field>
            <Field label="Total From">
              <Input
                className={s.control}
                type="number"
                contentBefore="$"
                input={{ className: ms.moneyText }}
                placeholder="Any"
                value={draft.minTotal}
                onChange={(_, d) => set('minTotal', d.value)}
              />
            </Field>
            <Field label="Total To">
              <Input
                className={s.control}
                type="number"
                contentBefore="$"
                input={{ className: ms.moneyText }}
                placeholder="Any"
                value={draft.maxTotal}
                onChange={(_, d) => set('maxTotal', d.value)}
              />
            </Field>
          </div>
        )}
      </FilterDialog>
    </>
  )
}
